import React, { useState } from 'react';
import { Box, List, ListItemText, ListItem, Typography, Stack } from '@mui/material';
import { AuthPermissions } from 'skipify-types';

import PageWrapper from '../../common/PageWrapper';
import { Alert } from '../Paylinks/components/Alert';
import { CreateChildMerchantForm } from './components/CreateChildMerchantForm';
import {
  useCreateChildMerchantMutation,
  useGetConfigsQuery,
  useGetCredentialsQuery,
  useGetMerchantQuery,
} from '../../store/modules/merchant/api';
import { useMerchant, isMerchantIdParentPickerRow } from '../../contexts/MerchantContext';
import {
  ConfigResponse,
  CreateChildMerchantBody,
  CreateChildMerchantResponse,
  NonAcceptedCardBrandsValue,
  findPaymentGatewayType,
  ShopperCredentialSharingValue,
} from '../../store/modules/merchant/types';
import { toast } from '../../util/toast';
import { MerchantCreatedDialog } from '../../common/MerchantCreate';

export const CreateChildMerchant = () => {
  const {
    merchantId,
    directLineage,
    refreshMerchantAccess,
    canAccess,
    isChildMerchantOnlyUser,
    parentMerchants,
  } = useMerchant();
  const hasCreateAccess = canAccess(AuthPermissions.CREATE_MERCHANTS);
  const { data: credentials, isLoading: isLoadingPaymentCredentials } = useGetCredentialsQuery(
    { merchantId, type: ['payment'] },
    { skip: !merchantId },
  );
  const { data: merchant } = useGetMerchantQuery(merchantId, {
    skip: !merchantId,
  });
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });
  const getConfig = <T,>(name: string) =>
    (configs ?? []).find((config) => config.name === name) as ConfigResponse<T> | undefined;
  const nonAcceptedCardBrands = getConfig<NonAcceptedCardBrandsValue>('NON_ACCEPTED_CARD_BRANDS');
  const shopperCredentialSharingConfig = getConfig<ShopperCredentialSharingValue>(
    'SHOPPER_CREDENTIAL_ACCESS',
  );

  const defaultPaymentGateway = credentials?.payment?.credentials.find(
    (cred) => cred.credentialId === merchant?.externalCredentials.payment,
  );
  const [createChildMerchant, { isLoading: isSubmitting }] = useCreateChildMerchantMutation();
  const [createdMerchant, setCreatedMerchant] = useState<CreateChildMerchantResponse | null>();

  const isTopLevelMerchant = directLineage?.length === 0;

  const merchantIdIsParentPickerRow = isMerchantIdParentPickerRow(merchantId, parentMerchants);
  const showHierarchyWarningAlert = !isChildMerchantOnlyUser || !merchantIdIsParentPickerRow;

  return (
    <PageWrapper
      title="Create New Merchant"
      description="Create new child merchant accounts"
      isLoading={!merchantId || isLoadingPaymentCredentials || isLoadingConfigs}
      key={merchantId}
    >
      <Stack
        spacing={4}
        sx={{
          maxWidth: { xs: '100%', md: '500px', lg: '800px' },
        }}
      >
        <Box>
          <Box
            sx={{
              '& p, span': {
                color: 'common.black',
                fontSize: '14px',
              },
              '& b': {
                fontWeight: 600,
              },
            }}
          >
            <Typography>
              You&apos;re about to create a new child merchant account. Please note the following
              before proceeding:
            </Typography>
            <List
              sx={{
                listStyleType: 'disc',
                color: 'common.black',
                pl: 2,
                '& li': {
                  display: 'list-item',
                  pl: 1,
                },
              }}
            >
              <ListItem>
                <ListItemText>
                  This account will be linked directly under your current (parent) merchant account.
                  You <b>must be a parent-level merchant</b> to use this feature.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>
                  Certain configuration settings (e.g. settlement type, tax behavior, etc.) may be
                  <b> automatically inherited</b> from the parent account to ensure consistency
                  across your hierarchy.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>
                  You&apos;ll be asked to enter basic merchant details and provide PSP credentials
                  based on the parent account&apos;s selected payment gateway.
                </ListItemText>
              </ListItem>
            </List>
          </Box>
          {showHierarchyWarningAlert ? (
            <Alert severity="warning">
              <Stack spacing={1}>
                <Typography sx={{ fontSize: '14px' }}>
                  Please ensure you&apos;re respecting the account hierarchy and only creating
                  accounts that should sit directly beneath your current merchant.
                </Typography>
                {!isTopLevelMerchant && (
                  <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                    Reminder: This isn’t a top-level merchant. New accounts will attach to &quot;
                    {merchant?.name}&quot; (your current account), not &quot;
                    {directLineage?.[0]?.name}
                    &quot;.
                  </Typography>
                )}
              </Stack>
            </Alert>
          ) : null}
        </Box>
        <CreateChildMerchantForm
          paymentGateway={
            findPaymentGatewayType(defaultPaymentGateway?.systemOfOrigin) ?? 'Express'
          }
          parentMerchantName={merchant?.name ?? ''}
          nonAcceptedCardBrands={nonAcceptedCardBrands?.value?.nonAcceptedCardBrands}
          isPaymentGatewayDataRequired={
            !shopperCredentialSharingConfig?.value.networkTokenSharingEnabled &&
            !shopperCredentialSharingConfig?.value.panSharingEnabled
          }
          isSubmitting={isSubmitting}
          readOnly={!hasCreateAccess}
          onSubmit={async (formValues) => {
            const requestBody: CreateChildMerchantBody = {
              name: formValues.name,
              partnerMerchantId: merchantId,
              credentials: [],
              configs: [],
              urls: [],
            };

            try {
              if (formValues.paymentGateway !== 'none') {
                if (!formValues.paymentGatewayData) {
                  // Validation should prevent this from happening
                  throw new Error('Payment gateway data is required');
                }

                requestBody.credentials?.push({
                  credentialType: 'payment',
                  systemOfOrigin: formValues.paymentGateway,
                  data: formValues.paymentGatewayData,
                });
              }

              if (!formValues.inheritNonAcceptedCardBrands) {
                requestBody.configs?.push({
                  name: 'NON_ACCEPTED_CARD_BRANDS',
                  value: { nonAcceptedCardBrands: formValues.nonAcceptedCardBrands },
                  enabled: true,
                });
              }

              if (formValues.domain) {
                requestBody.urls?.push({
                  backendUrl: formValues.domain,
                  url: formValues.domain,
                });
              }

              const result = await createChildMerchant(requestBody).unwrap();
              setCreatedMerchant(result);
              await refreshMerchantAccess();
            } catch (error) {
              toast.error(
                'Something went wrong while creating the child merchant. Please try again later.',
              );
              // throw the error so the form knows not to reset
              throw error;
            }
          }}
        />
      </Stack>
      {createdMerchant && (
        <MerchantCreatedDialog
          createdMerchant={createdMerchant}
          parentMerchantName={merchant?.name ?? ''}
          open={!!createdMerchant}
          onClose={() => setCreatedMerchant(null)}
        />
      )}
    </PageWrapper>
  );
};
